import { ActionsTotalFrequencies, Card, GameState, Player, PokerAction, PokerPlayerAction, Rank, Suit } from './types';
import { CARD_POSITIONS, SEAT_POSITIONS, BET_POSITIONS, POSITIONS_CLOCKWISE_PREFLOP, STATUS_COLORS, STATUS_TEXT_OFFSETS, calculateRelativeSeatIndex } from './positions';
import { getCardImageSrc, DECK_POSITION } from './card-renderer';
import { handToNotation } from './game';
import { DEAL_ANIMATION_MAX_MS } from './shared_constants';

export class TableRenderer {
   private canvas: HTMLCanvasElement;
   private ctx: CanvasRenderingContext2D;
   private seatRadius: number;
   private centerX: number;
   private centerY: number;
   private seatPositions: typeof SEAT_POSITIONS;
   private isDragging: boolean = false;
   private selectedSeat: string | null = null;
   private selectedCard: { playerIndex: number, cardIndex: number } | null = null;
   private selectedBet: number | null = null;
   private dragOffsetX: number = 0;
   private dragOffsetY: number = 0;
   // Card size variables
   private userCardWidth: number = 69;
   private userCardHeight: number = 104;
   private otherCardWidth: number = 36;
   private otherCardHeight: number = 54;
   private cardPositions: { [key: string]: { x: number, y: number } } = { ...CARD_POSITIONS };
   private betPositions: { [key: string]: { x: number, y: number } } = { ...BET_POSITIONS };

   // Card dealing animation
   private isAnimatingDeal: boolean = false;
   private dealAnimationStartTime: number = 0;
   private dealAnimationDuration: number = 1500; // milliseconds for the entire animation
   private dealAnimationProgress: number = 0;
   private animationFrameId: number = 0;
   private currentHandAnimationComplete: boolean = false; // Flag to track if animation has completed for current hand
   
   private gameState: GameState | null = null;
   private tableImage: HTMLImageElement | null = null;
   private positionImages: {[key: string]: HTMLImageElement} = {};
   private cardBackImage: HTMLImageElement;
   private cardImages: {[key: string]: HTMLImageElement} = {};

   private tableWidth: number = 800;
   private tableHeight: number = 500;
   private xOffset: number;
   private yOffset: number;
   private lastHandId: number | undefined;

   constructor(canvasId: string) {
      this.canvas = document.getElementById(canvasId) as HTMLCanvasElement;
      if (!this.canvas) {
         throw new Error(`Canvas element with ID ${canvasId} not found`);
      }
      this.xOffset = (this.canvas.width - this.tableWidth) / 2;
      this.yOffset = (this.canvas.height - this.tableHeight) / 2;

      const ctx = this.canvas.getContext('2d');
      if (!ctx) {
         throw new Error('Failed to get 2D context from canvas');
      }

      this.ctx = ctx;
      // this.tableWidth = this.canvas.width * 0.8;
      // this.tableHeight = this.canvas.height * 0.6;
      this.seatRadius = 68;
      this.centerX = this.canvas.width / 2;
      this.centerY = this.canvas.height / 2;

      // Initialize seat positions
      this.seatPositions = JSON.parse(JSON.stringify(SEAT_POSITIONS));

      // Load the table image
      this.tableImage = new Image();
      this.tableImage.src = '/images/poker-table-5-edit.png';

      // Load position images
      const positions_img_names = ['bb', 'sb', 'btn', 'co', 'hj', 'lj', 'utg', 'utg_1', 'utg_2', 'blank'];
      positions_img_names.forEach(pos => {
         const img = new Image();
         img.src = `./images/${pos}.png`;
         this.positionImages[pos.toUpperCase()] = img;
      });

      // Load card back image
      this.cardBackImage = new Image();
      this.cardBackImage.src = './images/cards/back_4.png';

      // Preload all card images
      this.preloadCardImages();

      // Set up event listeners for drag functionality
      this.canvas.addEventListener('mousedown', this.handleMouseDown.bind(this));
      this.canvas.addEventListener('mousemove', this.handleMouseMove.bind(this));
      this.canvas.addEventListener('mouseup', this.handleMouseUp.bind(this));
   }

   public render(gameState: GameState): void {
      // console.log("render()");

      if (!gameState) {
         return;
      }
      this.gameState = gameState;

      if (this.lastHandId !== this.gameState.handId) {
         this.currentHandAnimationComplete = false;
      }
      this.lastHandId = this.gameState.handId;

      
      // Start animation only if:
      // 1. Not currently animating
      // 2. we're at the start of a hand
      // 3. We haven't already completed an animation for this hand
      // console.log('this.isAnimatingDeal:', this.isAnimatingDeal);
      // console.log('gameState.playerActions.length:', gameState.playerActions.length);
      // console.log('gameState.isHandComplete:', gameState.isHandComplete);
      // console.log('this.currentHandAnimationComplete:', this.currentHandAnimationComplete);
      if (!this.isAnimatingDeal && 
          (gameState.playerActions.length === 0) && 
          !this.currentHandAnimationComplete) {
         this.startDealAnimation();
      }

      this.clear();

      this.drawTitle();
      this.drawSubtitle();
      this.drawTable();
      this.drawPot(gameState.potSize);
      
      // If we're animating the deal, draw the animated cards
      if (this.isAnimatingDeal) {
         this.drawAnimatedDeal(gameState);
      } else {
         // Otherwise draw the normal seats with cards
         this.drawSeats(gameState.players, gameState.userHand, gameState.currentPlayerIndex);
      }

      if (gameState.isHandComplete) {
         this.drawHandCompleteMessage();
      } else {
         this.hideHandCompleteMessage();
      }
   }
   
   public setDealAnimationDuration(duration: number): void {
      this.dealAnimationDuration = Math.max(100, Math.min(DEAL_ANIMATION_MAX_MS, duration));
   }
   
   private startDealAnimation(): void {
      if (this.isAnimatingDeal) return;
      console.log("startDealAnimation()");

      if (!this.gameState) {
         console.error("!this.gameState");
      }
      if (!this.gameState?.stats) {
         console.error("!this.gameState?.stats");
      }
      if (!this.gameState?.stats?.roundNumber) {
         console.error("!this.gameState?.stats?.roundNumber");
      }
      
      console.log("Starting deal animation for round:", this.gameState?.stats.roundNumber);
      
      this.isAnimatingDeal = true;
      this.dealAnimationStartTime = performance.now();
      this.dealAnimationProgress = 0;
      
      // Cancel any previous animation frame
      if (this.animationFrameId) {
         cancelAnimationFrame(this.animationFrameId);
      }
      
      // Start the animation loop
      this.animateDeal();
      
      // // Add a safeguard timeout to ensure animation completes even if there are issues
      // setTimeout(() => {
      //    if (this.isAnimatingDeal) {
      //       console.warn('Animation safeguard triggered - animation taking too long');
      //       this.isAnimatingDeal = false;
      //       this.currentHandAnimationComplete = true;
      //       cancelAnimationFrame(this.animationFrameId);
            
      //       // Force final render
      //       if (this.gameState) {
      //          this.render(this.gameState);
      //       }
      //    }
      // }, this.dealAnimationDuration * 1.5); // 50% extra time as safety margin
   }
   
   private animateDeal(): void {
      const currentTime = performance.now();
      const elapsedTime = currentTime - this.dealAnimationStartTime;
      
      this.dealAnimationProgress = Math.min(elapsedTime / this.dealAnimationDuration, 1);
      
      if (this.gameState) {
         this.clear();
         this.drawTitle();
         this.drawSubtitle();
         this.drawTable();
         this.drawPot(this.gameState.potSize);
         this.drawAnimatedDeal(this.gameState);
      }
      
      if (this.dealAnimationProgress < 1) {
         this.animationFrameId = requestAnimationFrame(() => this.animateDeal());
      } else {
         console.log("Animation complete for round:", this.gameState?.stats.roundNumber);
         this.isAnimatingDeal = false;
         // Mark the animation as complete for this hand
         this.currentHandAnimationComplete = true;
         // Render one final time with the completed state
         if (this.gameState) {
            this.render(this.gameState);
         }
      }
   }   

   private drawAnimatedDeal(gameState: GameState): void {
      const userPlayer = gameState.players.find(p => p.isUser);
      if (!userPlayer) return;
      
      this.drawPlayersWithoutCards(gameState);
      
      // Get active players (we'll deal cards to them)
      const activePlayers = gameState.players.filter(p => p.status !== 'folded');
      if (activePlayers.length === 0) return;
      
      const totalCardsToAnimate = activePlayers.length * 2; // Each player gets 2 cards
      
      const deckX = DECK_POSITION.x + this.xOffset;
      const deckY = DECK_POSITION.y + this.yOffset;
      
      // We'll divide the animation into two phases: 
      // 1. First card to each player (0-0.5 progress)
      // 2. Second card to each player (0.5-1.0 progress)
      
      // How many cards have been dealt so far
      let cardsDealt = Math.floor(this.dealAnimationProgress * totalCardsToAnimate);
      const isFirstRound = this.dealAnimationProgress <= 0.5;
      
      // Which card index we're dealing (0 for first card, 1 for second)
      const currentCardIndex = isFirstRound ? 0 : 1;
      
      // Calculate which player we're dealing to
      const playersPerRound = activePlayers.length;
      const playerProgressInRound = isFirstRound 
         ? this.dealAnimationProgress * 2 * playersPerRound 
         : (this.dealAnimationProgress - 0.5) * 2 * playersPerRound;
      
      const currentPlayerIndex = Math.min(Math.floor(playerProgressInRound), playersPerRound - 1);
      
      // Calculate the progress of the current card being dealt (0 to 1)
      const cardProgress = playerProgressInRound - Math.floor(playerProgressInRound);
      
      
      // If we're in the second round, draw all first-round cards
      if (!isFirstRound) {
         for (let i = 0; i < activePlayers.length; i++) {
            this.drawAnimatedCard(activePlayers[i], userPlayer, 0, 1);
         }
      }

      // Draw all cards that have already been fully dealt (animated only)
      for (let i = 0; i < currentPlayerIndex; i++) {
         // Draw first card for current round
         this.drawAnimatedCard(activePlayers[i], userPlayer, currentCardIndex, 1);
      }
      
      // Draw the current card that's in motion
      if (currentPlayerIndex < activePlayers.length) {
         this.drawAnimatedCard(activePlayers[currentPlayerIndex], userPlayer, currentCardIndex, cardProgress);
      }
      
      // Draw remaining deck of cards in the center
      const remainingCards = Math.max(0, totalCardsToAnimate - cardsDealt - 1);
      this.drawDeckPile(deckX, deckY, remainingCards);
   }
   
   private drawPlayersWithoutCards(gameState: GameState): void {
      const userPlayer = gameState.players.find(p => p.isUser);
      if (!userPlayer) return;
      
      // Draw all players but skip drawing their cards
      for (const player of gameState.players) {
         let x, y;
         
         if (player.isUser) {
            x = this.seatPositions['USER'].x;
            y = this.seatPositions['USER'].y;
         } else {
            const seatIndex = calculateRelativeSeatIndex(player, userPlayer);
            const seatMap = ['USER', 'seat1', 'seat2', 'seat3', 'seat4', 'seat5', 'seat6', 'seat7', 'seat8'];
            const seatToUse = seatMap[seatIndex];
            
            if (!this.seatPositions[seatToUse]) {
               continue;
            }
            
            x = this.seatPositions[seatToUse].x;
            y = this.seatPositions[seatToUse].y;
         }
         
         // Draw the seat without cards
         this.drawSeatOnly(x + this.xOffset, y + this.yOffset, player, gameState.players.indexOf(player) === gameState.currentPlayerIndex);
      }
   }
   
   private drawSeatOnly(x: number, y: number, player: Player, isActive = false): void {
      // Similar to drawSeat but without drawing cards
      const posImageKey = player.position ? player.position.replace('+', '_') : '';
      const posImage = this.positionImages[posImageKey] || this.positionImages['BLANK'];
      
      if (posImage && posImage.complete) {
         const imgX = x - (this.seatRadius);
         const imgY = y - (this.seatRadius);
         
         this.ctx.imageSmoothingEnabled = true;
         this.ctx.imageSmoothingQuality = "high";
         this.ctx.drawImage(posImage, imgX, imgY, this.seatRadius * 2, this.seatRadius * 2);
         
         if (isActive) {
            const tempCanvas = document.createElement('canvas');
            tempCanvas.width = this.ctx.canvas.width;
            tempCanvas.height = this.ctx.canvas.height;
            const tempCtx = tempCanvas.getContext('2d')!;
            for (let i = 8; i >= 1; i--) {
               tempCtx.save();
               tempCtx.globalAlpha = 0.08;
               tempCtx.beginPath();
               tempCtx.arc(x, y, this.seatRadius * 0.7 + i * 2, 0, 2 * Math.PI);
               tempCtx.fillStyle = `hsl(${50}, 98%, 60%)`;
               tempCtx.shadowColor = `hsl(${55}, 100%, 58%)`;
               tempCtx.shadowBlur = 30 + i * 6;
               tempCtx.fill();
               tempCtx.restore();
            }
            this.ctx.save();
            this.ctx.globalAlpha = 1.0;
            this.ctx.globalCompositeOperation = 'overlay';
            this.ctx.drawImage(tempCanvas, 0, 0);
            this.ctx.globalCompositeOperation = 'source-over';
            this.ctx.restore();
         }
         
         // Draw player status if needed
         this.ctx.fillStyle = STATUS_COLORS[player.status] || '#ffffff';
         this.ctx.font = '12px Arial';
         this.ctx.textAlign = 'center';
         if (player.status !== 'active') {
            const userPlayer = this.gameState?.players.find((p: Player) => p.isUser);
            if (userPlayer) {
               const seatIndex = calculateRelativeSeatIndex(player, userPlayer);
               const statusKey = `player${seatIndex}_status`;
               const statusOffset = STATUS_TEXT_OFFSETS[statusKey] || { x: 0, y: 0 };
               this.ctx.fillText(player.status, x + statusOffset.x, y + 37 + statusOffset.y);
            } else {
               this.ctx.fillText(player.status, x, y + 37);
            }
         }
         
         // Draw bet amount if needed
         const userPlayer = this.gameState?.players.find((p: Player) => p.isUser);
         if (userPlayer && player.betAmount > 0) {
            const seatIndex = calculateRelativeSeatIndex(player, userPlayer);
            const betKey = `player${seatIndex}_bet`;
            
            const defaultBetX = x;
            const defaultBetY = y + this.seatRadius + 15;
            
            if (!this.betPositions[betKey]) {
               this.betPositions[betKey] = { x: defaultBetX, y: defaultBetY };
            }
            
            const betX = this.betPositions[betKey].x;
            const betY = this.betPositions[betKey].y;
            
            this.ctx.fillStyle = '#ffc107';
            this.ctx.beginPath();
            this.ctx.arc(betX, betY, 12, 0, 2 * Math.PI);
            this.ctx.fill();
            
            this.ctx.fillStyle = 'black';
            this.ctx.font = '10px Arial';
            this.ctx.fillText(player.betAmount.toString(), betX, betY + 3);
         }
      }
   }
   
   private drawAnimatedCard(player: Player, userPlayer: Player, cardIndex: number, progress: number): void {
      // Get the target position for this card
      const seatIndex = calculateRelativeSeatIndex(player, userPlayer);
      const cardKey = `player${seatIndex}_card${cardIndex}`;
      
      // Use appropriate card size based on player type
      const cardWidth = player.isUser ? this.userCardWidth : this.otherCardWidth;
      const cardHeight = player.isUser ? this.userCardHeight : this.otherCardHeight;
      
      // Default positions if none set
      const defaultCardsY = this.seatPositions[seatIndex === 0 ? 'USER' : `seat${seatIndex}`].y - 65 + this.yOffset;
      const defaultCard1X = this.seatPositions[seatIndex === 0 ? 'USER' : `seat${seatIndex}`].x - cardWidth - 2.5 + this.xOffset;
      const defaultCard2X = this.seatPositions[seatIndex === 0 ? 'USER' : `seat${seatIndex}`].x + 2.5 + this.xOffset;
      
      // Source position (deck position)
      const sourceX = DECK_POSITION.x + this.xOffset;
      const sourceY = DECK_POSITION.y + this.yOffset;
      
      // Target position
      let targetX, targetY;
      
      if (cardIndex === 0) {
         // First card
         const cardPos = this.cardPositions[cardKey];
         if (cardPos) {
            targetX = cardPos.x;
            targetY = cardPos.y;
         } else {
            targetX = defaultCard1X;
            targetY = defaultCardsY;
            // Store default position
            this.cardPositions[cardKey] = { x: targetX, y: targetY };
         }
      } else {
         // Second card
         const cardPos = this.cardPositions[cardKey];
         if (cardPos) {
            targetX = cardPos.x;
            targetY = cardPos.y;
         } else {
            targetX = defaultCard2X;
            targetY = defaultCardsY;
            // Store default position
            this.cardPositions[cardKey] = { x: targetX, y: targetY };
         }
      }
      
      // Get current position based on progress
      const currentX = sourceX + (targetX - sourceX) * progress;
      const currentY = sourceY + (targetY - sourceY) * progress;
      
      // Add a slight arc to the card movement using quadratic curve motion
      const arcHeight = 200;
      const arcProgress = progress < 0.5 ? progress * 2 : (1 - progress) * 2;
      const arcY = arcProgress * arcHeight;
      const finalY = currentY - arcY;
      
      // Add slight rotation based on progress
      const maxRotation = Math.PI * 2; // 2 full rotations
      const rotation = progress * maxRotation;
      
      // Draw the card
      this.ctx.save();
      
      // Move to the current position and apply rotation
      this.ctx.translate(currentX + cardWidth / 2, finalY + cardHeight / 2);
      this.ctx.rotate(rotation);
      this.ctx.translate(-cardWidth / 2, -cardHeight / 2);
      
      if (player.isUser && player.hand && player.hand.length === 2 && progress === 1) {
         // Draw the user's actual card
         const cardObj = player.hand[cardIndex];
         const imgKey = `${cardObj.rank}_${cardObj.suit}`;
         if (this.cardImages[imgKey]) {
            this.ctx.drawImage(this.cardImages[imgKey], 0, 0, cardWidth, cardHeight);
         }
      } else {
         // Draw a card back
         if (this.cardBackImage && this.cardBackImage.complete) {
            this.ctx.drawImage(this.cardBackImage, 0, 0, cardWidth, cardHeight);
         }
      }
      
      this.ctx.restore();
   }
   
   private drawDeckPile(x: number, y: number, count: number): void {
      if (!this.cardBackImage || !this.cardBackImage.complete) return;
      
      const cardWidth = this.otherCardWidth;
      const cardHeight = this.otherCardHeight;
      
      // Draw a stack of cards based on count (max 5 visible cards in stack)
      const visibleCards = Math.min(count, 5);
      const offsetPerCard = 2; // Offset for stacking effect
      
      for (let i = visibleCards - 1; i >= 0; i--) {
         const offsetX = i * offsetPerCard;
         const offsetY = i * offsetPerCard;
         
         this.ctx.drawImage(
            this.cardBackImage, 
            x - cardWidth / 2 - offsetX, 
            y - cardHeight / 2 - offsetY, 
            cardWidth, 
            cardHeight
         );
      }
      
      // Show count if more than 5 cards
      if (count > 5) {
         this.ctx.fillStyle = 'white';
         this.ctx.font = '12px "Century Gothic", "Segoe UI", "Art Nouveau", Arial, sans-serif';
         this.ctx.textAlign = 'center';
         this.ctx.fillText(`${count} cards`, x, y + cardHeight / 2 + 15);
      }
   }

   private clear(): void {
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
   }

   private drawTable(): void {
      if (this.tableImage) {
         this.ctx.drawImage(this.tableImage, this.xOffset, this.yOffset, this.tableWidth, this.tableHeight);
      }
   }

   private drawTitle(): void {
      const title = "Poker Cash-Game Preflop Trainer";
      this.ctx.save();
      this.ctx.font = "bold 36px 'Parisienne', 'Algerian', 'Copperplate', 'Times New Roman', serif";
      this.ctx.fillStyle = "#e7c77f";
      this.ctx.textAlign = "center";
      this.ctx.textBaseline = "top";
      this.ctx.shadowColor = "#7c6742";
      this.ctx.shadowBlur = 6;
      this.ctx.lineWidth = 2;
      this.ctx.strokeStyle = "#3a2c11";
      this.ctx.strokeText(title, this.canvas.width / 2 - 10, 20);
      this.ctx.shadowBlur = 0;
      this.ctx.fillText(title, this.canvas.width / 2 - 10, 20);
      this.ctx.restore();
   }

   private drawSubtitle(): void {
      const subtitle = "9-max 100BB";
      this.ctx.save();
      this.ctx.font = "22px 'Parisienne', 'Algerian', 'Copperplate', 'Times New Roman', serif";
      this.ctx.fillStyle = "#d4b560";
      this.ctx.textAlign = "center";
      this.ctx.textBaseline = "top";
      this.ctx.shadowColor = "#7c6742";
      this.ctx.shadowBlur = 4;
      this.ctx.lineWidth = 1.5;
      this.ctx.strokeStyle = "#37280e";
      this.ctx.strokeText(subtitle, this.canvas.width / 2 - 23, 65);
      this.ctx.shadowBlur = 0;
      this.ctx.fillText(subtitle, this.canvas.width / 2 - 23, 65);
      this.ctx.restore();
   }

   private drawPot(potSize: number): void {
       const text = `Pot: ${potSize.toFixed(1)} BB`;
       this.ctx.save();
       this.ctx.font = "25px 'Parisienne', 'Copperplate', 'Times New Roman', serif";
       this.ctx.textAlign = "center";
       this.ctx.textBaseline = "top";
       this.ctx.fillStyle = "#ede4c4";
       this.ctx.shadowColor = "#a6987d";
       this.ctx.shadowBlur = 4;
       this.ctx.lineWidth = 2;
       // this.ctx.strokeStyle = "#867768";
      this.ctx.strokeStyle = "#37280e";
       const iconL = "";
       const iconR = "";
       const textX = this.canvas.width / 2;
       const textY = this.centerY - 21;
       const textWidth = this.ctx.measureText(text).width;
       // Draw icons
       this.ctx.font = "20px 'Parisienne', serif";
       this.ctx.globalAlpha = 0.76;
       this.ctx.strokeText(iconL, textX - textWidth / 2 - 30, textY + 2);
       this.ctx.fillText(iconL, textX - textWidth / 2 - 30, textY + 2);
       this.ctx.strokeText(iconR, textX + textWidth / 2 + 30, textY + 2);
       this.ctx.fillText(iconR, textX + textWidth / 2 + 30, textY + 2);
   
       // Draw the Pot text with subtle shadow
       this.ctx.font = "25px 'Parisienne', 'Copperplate', 'Times New Roman', serif";
       this.ctx.globalAlpha = 0.93;
       this.ctx.strokeText(text, textX, textY);
       this.ctx.shadowColor = "#232a3b";
       this.ctx.shadowBlur = 0;
       this.ctx.fillText(text, textX, textY);
       this.ctx.restore();
   }

   private drawSeats(players: Player[], userHand: Card[], currentPlayerIndex: number): void {
      // Find the user
      const userPlayer = players.find(p => p.isUser);
      if (!userPlayer) return;

      // Check if user position is valid
      if (POSITIONS_CLOCKWISE_PREFLOP.indexOf(userPlayer.position) === -1) return;

      // Draw all players
      for (const player of players) {
         let x, y;

         if (player.isUser) {
            // User is always at the bottom USER position
            x = this.seatPositions['USER'].x;
            y = this.seatPositions['USER'].y;
         } else {
            // Calculate relative position and map to seat
            const seatIndex = calculateRelativeSeatIndex(player, userPlayer);
            
            // Map to appropriate seat
            const seatMap = ['USER', 'seat1', 'seat2', 'seat3', 'seat4', 'seat5', 'seat6', 'seat7', 'seat8'];
            const seatToUse = seatMap[seatIndex];

            // Check if the seat position exists before accessing x and y
            if (!this.seatPositions[seatToUse]) {
                console.warn(`Seat position ${seatToUse} not found for player position ${player.position}. seatIndex: ${seatIndex}`);
                continue; // Skip this player if seat position is undefined
            }
            
            x = this.seatPositions[seatToUse].x;
            y = this.seatPositions[seatToUse].y;
         }

         this.drawSeat(
            x + this.xOffset,
            y + this.yOffset,
            player,
            player.isUser ? userHand : undefined,
            players.indexOf(player) === currentPlayerIndex
         );
      }
   }

   private drawSeat(x: number, y: number, player: Player, hand?: Card[], isActive = false): void {
      // Get position image
      const posImageKey = player.position ? player.position.replace('+', '_') : '';
      const posImage = this.positionImages[posImageKey] || this.positionImages['BLANK'];

      if (posImage && posImage.complete) {
         // Calculate position so the center of the image is at x,y
         const imgX = x - (this.seatRadius);
         const imgY = y - (this.seatRadius);

         // Draw position image
         this.ctx.imageSmoothingEnabled = true;
         this.ctx.imageSmoothingQuality = "high";
         this.ctx.drawImage(posImage, imgX, imgY, this.seatRadius * 2, this.seatRadius * 2);

         if (isActive) {
            const tempCanvas = document.createElement('canvas');
            tempCanvas.width = this.ctx.canvas.width;
            tempCanvas.height = this.ctx.canvas.height;
            const tempCtx = tempCanvas.getContext('2d')!;
            for (let i = 8; i >= 1; i--) {
               tempCtx.save();
               tempCtx.globalAlpha = 0.08;
               tempCtx.beginPath();
               tempCtx.arc(x, y, this.seatRadius * 0.7 + i * 2, 0, 2 * Math.PI);
               tempCtx.fillStyle = `hsl(${50}, 98%, 60%)`;
               tempCtx.shadowColor = `hsl(${55}, 100%, 58%)`;
               tempCtx.shadowBlur = 30 + i * 6;
               tempCtx.fill();
               tempCtx.restore();
            }
            this.ctx.save();
            this.ctx.globalAlpha = 1.0;
            this.ctx.globalCompositeOperation = 'overlay';
            this.ctx.drawImage(tempCanvas, 0, 0);
            this.ctx.globalCompositeOperation = 'source-over';
            this.ctx.restore();
         }

         if (player.status === 'folded') {
            const grayCanvas = document.createElement('canvas');
            grayCanvas.width = this.ctx.canvas.width;
            grayCanvas.height = this.ctx.canvas.height;
            const grayCtx = grayCanvas.getContext('2d')!;
            grayCtx.save();
            grayCtx.globalAlpha = 0.85;
            grayCtx.beginPath();
            grayCtx.arc(x, y, this.seatRadius * 0.83, 0, 2 * Math.PI);
            grayCtx.fillStyle = '#888';
            grayCtx.filter = 'blur(3px) grayscale(0.65)';
            grayCtx.fill();
            grayCtx.restore();
            this.ctx.save();
            this.ctx.globalAlpha = 1.0;
            this.ctx.globalCompositeOperation = 'saturation';
            this.ctx.drawImage(grayCanvas, 0, 0);
            this.ctx.globalCompositeOperation = 'source-over';
            this.ctx.restore();
         } else if (player.status === 'raised') {
            const tempCanvas = document.createElement('canvas');
            tempCanvas.width = this.ctx.canvas.width;
            tempCanvas.height = this.ctx.canvas.height;
            const tempCtx = tempCanvas.getContext('2d')!;
            for (let i = 8; i >= 1; i--) {
               tempCtx.save();
               tempCtx.globalAlpha = 0.07;
               tempCtx.beginPath();
               tempCtx.arc(x, y, this.seatRadius * 0.7 + i * 2, 0, 2 * Math.PI);
               tempCtx.strokeStyle = `hsl(125, 98%, 48%)`;
               tempCtx.lineWidth = 1 + i * 1.6;
               tempCtx.shadowColor = `hsl(120, 100%, 48%)`;
               tempCtx.shadowBlur = 5 + i * 2;
               tempCtx.stroke();
               tempCtx.restore();
            }
            this.ctx.save();
            this.ctx.globalAlpha = 1.0;
            this.ctx.globalCompositeOperation = 'lighter';
            this.ctx.drawImage(tempCanvas, 0, 0);
            this.ctx.globalCompositeOperation = 'source-over';
            this.ctx.restore();
         }
      }

      // Draw status
      this.ctx.fillStyle = STATUS_COLORS[player.status] || '#ffffff';
      this.ctx.font = '12px Arial';
      this.ctx.textAlign = 'center';
      if (player.status !== 'active') {
         const userPlayer = this.gameState?.players.find((p: Player) => p.isUser);
         if (userPlayer) {
            const seatIndex = calculateRelativeSeatIndex(player, userPlayer);
            const statusKey = `player${seatIndex}_status`;
            const statusOffset = STATUS_TEXT_OFFSETS[statusKey] || { x: 0, y: 0 };
            this.ctx.fillText(player.status, x + statusOffset.x, y + 37 + statusOffset.y);
         } else {
            this.ctx.fillText(player.status, x, y + 37);
         }
      }

      const userPlayer = this.gameState?.players.find((p: Player) => p.isUser);
      if (!userPlayer) return;

      // Draw bet amount
      if (player.betAmount > 0) {
         const seatIndex = calculateRelativeSeatIndex(player, userPlayer);
         const betKey = `player${seatIndex}_bet`;
         
         // Use stored position or default if not set
         const defaultBetX = x;
         const defaultBetY = y + this.seatRadius + 15;
         
         // Get bet position from stored positions or use defaults
         if (!this.betPositions[betKey]) {
            this.betPositions[betKey] = { x: defaultBetX, y: defaultBetY };
         }
         
         const betX = this.betPositions[betKey].x;
         const betY = this.betPositions[betKey].y;
         
         // Draw yellow circle
         this.ctx.fillStyle = '#ffc107';
         this.ctx.beginPath();
         this.ctx.arc(betX, betY, 12, 0, 2 * Math.PI);
         this.ctx.fill();

         // Draw bet amount text
         this.ctx.fillStyle = 'black';
         this.ctx.font = '10px Arial';
         this.ctx.fillText(player.betAmount.toString(), betX, betY + 3);
      }
      
      const seatIndex = calculateRelativeSeatIndex(player, userPlayer);
      const card1Key = `player${seatIndex}_card0`;
      const card2Key = `player${seatIndex}_card1`;
      
      // Use the appropriate card size based on player type
      const cardWidth = player.isUser ? this.userCardWidth : this.otherCardWidth;
      // const cardHeight = player.isUser ? this.userCardHeight : this.otherCardHeight;
      
      // Default card positions if none set
      const defaultCardsY = y - 65;
      const defaultCard1X = x - cardWidth - 2.5;
      const defaultCard2X = x + 2.5;
      
      // Get card positions from the stored positions or use defaults
      // const randomOffset = () => ({ x: (Math.random() - 0.5) * 4, y: (Math.random() - 0.5) * 4 });
      const card1Base = this.cardPositions[card1Key];
      if (!card1Base) console.warn(`Defaulting card1 position for key: ${card1Key}`);
      const finalCard1Base = card1Base || { x: defaultCard1X, y: defaultCardsY };
      const card2Base = this.cardPositions[card2Key];
      if (!card2Base) console.warn(`Defaulting card2 position for key: ${card2Key}`);
      const finalCard2Base = card2Base || { x: defaultCard2X, y: defaultCardsY };
      const card1Pos = { x: finalCard1Base.x, y: finalCard1Base.y };
      const card2Pos = { x: finalCard2Base.x, y: finalCard2Base.y };
      // const card1Pos = { x: card1Base.x + randomOffset().x, y: card1Base.y + randomOffset().y };
      // const card2Pos = { x: card2Base.x + randomOffset().x, y: card2Base.y + randomOffset().y };
      
      // Store default positions if not already set
      if (!this.cardPositions[card1Key]) this.cardPositions[card1Key] = { x: defaultCard1X, y: defaultCardsY };
      if (!this.cardPositions[card2Key]) this.cardPositions[card2Key] = { x: defaultCard2X, y: defaultCardsY };

      if (player.isUser && hand) {
         if (hand && hand.length === 2) {
            // Use preloaded card images
            const card1Key = `${hand[0].rank}_${hand[0].suit}`;
            const card2Key = `${hand[1].rank}_${hand[1].suit}`;
            
            if (this.cardImages[card1Key]) {
               this.ctx.drawImage(this.cardImages[card1Key], card1Pos.x, card1Pos.y, this.userCardWidth, this.userCardHeight);
            }
            
            if (this.cardImages[card2Key]) {
               this.ctx.drawImage(this.cardImages[card2Key], card2Pos.x, card2Pos.y, this.userCardWidth, this.userCardHeight);
            }
         }
      } else {
         // Draw face-down cards for other players
         if (this.cardBackImage && this.cardBackImage.complete) {
            this.ctx.drawImage(this.cardBackImage, card1Pos.x, card1Pos.y, this.otherCardWidth, this.otherCardHeight);
            this.ctx.drawImage(this.cardBackImage, card2Pos.x, card2Pos.y, this.otherCardWidth, this.otherCardHeight);
         }
      }
   }

   private handCompleteElement: HTMLDivElement | null = null;
   private readonly handCompleteElementId = 'hand-complete-dialog';

   private getUserActionsOnly(): PokerPlayerAction[] {
      return this.gameState!.playerActions.filter(a => a.actor === 'USER');
   }

   private getAIActionsOnly(): PokerPlayerAction[] {
      return this.gameState!.playerActions.filter(a => a.actor === 'AI');
   }

   private drawHandCompleteMessage(): void {
      console.log("drawHandCompleteMessage()");
      // Create or reuse a floating DOM element
      if (!this.handCompleteElement) {
         this.handCompleteElement = document.createElement('div');
         this.handCompleteElement.id = this.handCompleteElementId;
         document.body.appendChild(this.handCompleteElement);
      }

      // Position the floating element over the canvas
      const rect = this.canvas.getBoundingClientRect();
      this.handCompleteElement.style.position = 'absolute';
      this.handCompleteElement.style.left = `${rect.left + this.centerX}px`;
      this.handCompleteElement.style.top = `${rect.top + this.centerY}px`;
      this.handCompleteElement.style.transform = 'translate(-50%, -50%)';
      this.handCompleteElement.style.backgroundColor = 'rgba(48,38,30,0.98)';
      this.handCompleteElement.style.borderRadius = '16px';
      this.handCompleteElement.style.display = 'flex';
      this.handCompleteElement.style.flexDirection = 'column';
      this.handCompleteElement.style.alignItems = 'center';
      this.handCompleteElement.style.color = '#F5DEB3';
      this.handCompleteElement.style.padding = '10px 20px';
      this.handCompleteElement.style.zIndex = '1000';
      this.handCompleteElement.style.overflowY = 'auto';
      this.handCompleteElement.style.border = '4px double gold';
      this.handCompleteElement.style.boxShadow = '0 0 20px 3px #e7c77f, 0 0 10px rgba(0,0,0,0.05)';
      this.handCompleteElement.style.width = 'fit-content';
      this.handCompleteElement.style.height = 'fit-content';
      this.handCompleteElement.style.maxWidth = '90vw';
      this.handCompleteElement.style.maxHeight = '90vh';
      
      // Clear previous content
      this.handCompleteElement.innerHTML = '';
      
      // Add title
      const title = document.createElement('h2');
      title.textContent = 'Hand Complete';
      title.style.fontFamily = "'Georgia', 'Times New Roman', Times, serif";
      title.style.fontWeight = 'bold';
      title.style.fontSize = '36px';
      title.style.color = '#F5DEB3';
      title.style.marginTop = '0';
      title.style.marginRight = '0';
      title.style.marginBottom = '20px';
      title.style.marginLeft = '0';
      this.handCompleteElement.appendChild(title);
      
      // Display action percentages if available
      if (this.gameState) {
         const userHand = this.gameState.userHand;
         if (userHand && userHand.length === 2 && this.gameState.userPosition !== null) {
            const handKey = handToNotation(userHand);
            const userPosition = this.gameState.userPosition;

            console.log('handKey:', handKey);
            console.log('userPosition:', userPosition);
            
            // Function to create actions section header
            const createSectionHeader = (title: string) => {
               const header = document.createElement('h3');
               header.textContent = title;
               header.style.fontFamily = "'Georgia', 'Times New Roman', Times, serif";
               header.style.fontWeight = 'bold';
               header.style.fontSize = '18px';
               header.style.color = '#D4AF37';
               header.style.margin = '15px 0 5px 0';
               header.style.width = '100%';
               header.style.textAlign = 'center';
               this.handCompleteElement!.appendChild(header);
               
               const divider = document.createElement('div');
               divider.style.width = '80%';
               divider.style.height = '1px';
               divider.style.backgroundColor = '#D4AF37';
               divider.style.margin = '0 0 10px 0';
               this.handCompleteElement!.appendChild(divider);
            };


            const analyzeActionWrapper = (betType: 'Opener' | '3-Bet' | '4-Bet', action: PokerAction): boolean => {
               if (!this.gameState) {
                  return false;
               }

               // NOTE: The user will never happen to do all three of these actions at the same time
               // It'll either be:
               // User opener, AI fold
               // AI opener, User 3Bet OR
               // User opener, AI 3Bet, User 4Bet
               //
               // with user folds:
               // User folds (counts as Opener?)
               // AI opens, User folds (counts as 3-Bet)
               // User opens, AI 3Bet, User folds (counts as 4Bet)
               //
               // The below ranges are all positioned so that the user is taking action,
               // they are not sequential actions all happening in 1 hand

               // TODO: update the opener and threeBet positions appropriately and clear them after each hand

               if (betType === 'Opener') {
                  console.log("Opener bet: this.gameState.ranges[" + userPosition + "]");
                  console.log(this.gameState.ranges);
                  const openerFrequencies = this.gameState.ranges[userPosition];
                  console.log("frequencies .ranges:");
                  console.log(openerFrequencies);
                  return analyzeAction(openerFrequencies[handKey].f, action, betType);
               } else if (betType === '3-Bet' && this.gameState.openerPosition) {
                  console.log("3-Bet: raiseRanges[" + this.gameState.openerPosition + "][" + userPosition + "]");
                  console.log(this.gameState.raiseRanges);
                  const threeBetFrequencies = this.gameState.raiseRanges[this.gameState.openerPosition][userPosition];
                  console.log("frequencies .raiseRanger:");
                  console.log(threeBetFrequencies);
                  return analyzeAction(threeBetFrequencies[handKey].f, action, betType);
               } else if (betType === '4-Bet' && this.gameState.threeBetPosition) {
                  console.log("4-Bet: fourBetRanges[" + userPosition + "][" + this.gameState.threeBetPosition + "]");
                  console.log(this.gameState.fourBetRanges);
                  const fourBetFrequencies = this.gameState.fourBetRanges[userPosition][this.gameState.threeBetPosition];
                  console.log("frequencies .fourBetRanges: ");
                  console.log(fourBetFrequencies);
                  return analyzeAction(fourBetFrequencies[handKey].f, action, betType);
               } else {
                  console.warn("Unknown betType: " + betType);
                  return false;
               }
            }
            
            // Function to analyze an action based on range data
            const analyzeAction = (frequencies: ActionsTotalFrequencies, actionMade: PokerAction, betType: string): boolean => {
               console.log("analyzeAction === actionMade: " + actionMade);
               
               // Create section header for this street
               createSectionHeader(`${betType} GTO Actions:`);
               
               // Array of possible actions with their values and keys
               const actionsArr = [
                  { name: 'Fold', value: frequencies.F, key: 'F' },
                  { name: 'Call', value: frequencies.C, key: 'C' },
                  { name: '2x Raise', value: frequencies.R2, key: 'R2' },
                  { name: '2.3x Raise', value: frequencies["R2.3"], key: 'R2.3' },
                  { name: '2.5x Raise', value: frequencies["R2.5"], key: 'R2.5' },
                  { name: '3x Raise', value: frequencies.R3, key: 'R3' },
                  { name: '6.5x Raise', value: frequencies["R6.5"], key: 'R6.5' },
                  { name: '7.5x Raise', value: frequencies["R7.5"], key: 'R7.5' },
                  { name: '10x Raise', value: frequencies.R10, key: 'R10' },
                  { name: '11x Raise', value: frequencies.R11, key: 'R11' },
                  { name: '11.5x Raise', value: frequencies["R11.5"], key: 'R11.5' },
                  { name: '12x Raise', value: frequencies.R12, key: 'R12' },
                  { name: '12.3x Raise', value: frequencies["R12.3"], key: 'R12.3' },
                  { name: '13x Raise', value: frequencies.R13, key: 'R13' },
                  { name: '13.5x Raise', value: frequencies["R13.5"], key: 'R13.5' },
                  { name: '18x Raise', value: frequencies.R18, key: 'R18' },
                  { name: '19x Raise', value: frequencies.R19, key: 'R19' },
                  { name: '20x Raise', value: frequencies.R20, key: 'R20' },
                  { name: '21x Raise', value: frequencies.R21, key: 'R21' },
                  { name: '22x Raise', value: frequencies.R22, key: 'R22' },
                  { name: '24.9x Raise', value: frequencies["R24.9"], key: 'R24.9' },
                  { name: '25x Raise', value: frequencies.R25, key: 'R25' },
                  { name: '26x Raise', value: frequencies.R26, key: 'R26' },
                  { name: '27x Raise', value: frequencies.R27, key: 'R27' },
                  { name: '28x Raise', value: frequencies.R28, key: 'R28' },
                  { name: 'All-in', value: frequencies.RAI, key: 'RAI' }
               ];
               // console.log("Possible actions BEFORE: " + actionsArr.map(a => a.name + ", " + a.value + ", " + a.key + "\n"));
               const actionMap = new Map<string, typeof actionsArr[0]>();
               actionsArr.filter(a => a.value !== undefined).forEach(action => {
                  actionMap.set(action.key, action);
               });

               const makeSureWeHaveTheseActions = [
                  { name: 'Fold', value: frequencies.F, key: 'F' },
                  { name: '3x Raise', value: frequencies.R3, key: 'R3' },
               ]

               if (actionMade === 'C') {
                  makeSureWeHaveTheseActions.push({ name: 'Call', value: frequencies.C ?? 0, key: 'C' });
               }
               if (actionMade === 'RAI') {
                  makeSureWeHaveTheseActions.push({ name: 'All-in', value: frequencies.RAI ?? 0, key: 'RAI' });
               }

               makeSureWeHaveTheseActions.forEach(action => {
                  if (!actionMap.has(action.key)) actionMap.set(action.key, action);
               });

               // console.log('Possible Actions (pre-consolidated):', Array.from(actionMap.values()));

               const consolidateRaiseKeys = ['R2', 'R2.3', 'R2.5', 'R3', 'R6.5', 'R7.5', 'R10', 'R11', 'R11.5', 'R12', 'R12.3', 'R13', 'R13.5', 'R19', 'R18', 'R20', 'R22', 'R24.9', 'R21', 'R25', 'R28', 'R26', 'R27']
               let r3Value = 0;
               for (const key of consolidateRaiseKeys) {
                  const action = actionMap.get(key);
                  if (action) {
                     r3Value += action.value ?? 0;
                     if (key !== 'R3') actionMap.delete(key);
                  }
               }
               if (actionMap.has('R3')) {
                  actionMap.get('R3')!.value = r3Value;
               } else {
                  actionMap.set('R3', { name: '3x Raise', value: r3Value, key: 'R3' });
               }

               // console.log('Possible Actions (post-consolidated):', Array.from(actionMap.values()));

               const actions = new Set(actionMap.values());
               
               // Find the highest percentage action
               const bestAction = Array.from(actions).reduce((prev, current) =>
                 (current.value! > prev.value!) ? current : prev,
                 { name: '', value: -1, key: '' }
               );
               console.log('bestAction:', bestAction);
               
               // Add each action percentage as a paragraph
               const addActionPercentage = (actionName: string, value: number, actionKey: string) => {
                  if (value !== undefined) {
                     const percentageText = document.createElement('p');
                     
                     // Build action text with appropriate indicator
                     let actionText = `${actionName}: ${(value * 100).toFixed(1)}%`;
                     
                     const artNouveauRed = 'rgba(203, 43, 62, 0.6)';
                     const artNouveauGreen = 'rgba(94, 187, 138, 0.6)';
                     const artNouveauGold = 'rgba(212, 175, 55, 0.6)';
                     percentageText.style.backgroundColor = "transparent";
                     // Add emoji based on whether user selected this action
                     if (actionMade === actionKey) {
                        if (actionMade === bestAction.key) {
                           // User made the best choice ✅
                           actionText += ' 🏆';
                           percentageText.style.backgroundColor = artNouveauGreen;
                        } else if (value === 0) {
                           // User made a 0% choice (blunder)
                           actionText += ' 💣';
                           percentageText.style.backgroundColor = artNouveauRed;
                        } else if (value < 0.15) {
                           // User made a low percentage choice
                           actionText += ' 🤏';
                           percentageText.style.backgroundColor = artNouveauGreen;
                        } else {
                           // not the highest % choice, but still correct
                           actionText += ' ✅';
                           percentageText.style.backgroundColor = artNouveauGreen;
                        }
                     }
                     
                     // Add arrow pointing to best action if user didn't pick it
                     if (actionKey === bestAction.key && actionMade !== bestAction.key) {
                        actionText += ' ⬅️';
                     }
                     
                     console.log("addActionPercentage() actName: " + actionName + " value: " + value + " key: " + actionKey);
                     console.log("-> " + actionText);

                     percentageText.textContent = actionText;
                     percentageText.style.fontFamily = "'Georgia', 'Times New Roman', Times, serif";
                     percentageText.style.fontSize = '16px';
                     percentageText.style.color = '#D4AF37';
                     percentageText.style.margin = '3px 0';
                     percentageText.style.textShadow = '1px 1px 3px rgba(0,0,0,0.8)';
                     percentageText.style.borderRadius = '18px';
                     percentageText.style.padding = '6px 18px';
                     percentageText.style.boxShadow = '0 2px 8px rgba(212,175,55,0.18)';
                     percentageText.style.border = `2px solid ${artNouveauGold}`;
                     if (this.handCompleteElement) {
                        this.handCompleteElement.appendChild(percentageText);
                     }
                  }
               };
               
               // Display the action percentages
               actions.forEach(a => {
                  addActionPercentage(a.name, a.value!, a.key);
               });
               
               return true;
            };

            // Possibilities:
            // User opener, AI fold
            // AI opener, User 3Bet OR
            // User opener, AI 3Bet, User 4Bet
            //
            // with user folds:
            // User folds (counts as Opener?)
            // AI opens, User folds (counts as 3-Bet)
            // User opens, AI 3Bet, User folds (counts as 4Bet)
            
            // this should always be true?
            if (this.getUserActionsOnly().length > 0) {

               // Get the ranges for the initial action
               const userMadeFirstAction = this.gameState.playerActions[0].actor === 'USER';
               const initialUserAction = this.getUserActionsOnly()[0].action;
               
               if (userMadeFirstAction) { 
                  console.log("userMadeFirstAction, so analyze 'Opener'...");
                  analyzeActionWrapper('Opener', initialUserAction)
               }
               
               // Check for 3-bet action (from gameState.raiseRanges)
               if (!userMadeFirstAction && this.getUserActionsOnly().length > 0) {
                  console.log("analyzing '3-bet'...")
                  // const raiseAction = this.getUserActionsOnly()[0].action;
                  analyzeActionWrapper('3-Bet', initialUserAction)
               }
               
               // Check for 4-bet action (from gameState.fourBetRanges)
               if (userMadeFirstAction && this.getUserActionsOnly().length > 1) {
                  console.log("analyzing '4-bet'...")
                  const fourBetAction = this.gameState.playerActions.filter((p) => p.actor === 'USER')[1].action;
                  console.log("--- fourBetAction: " + fourBetAction);
                  console.log(this.gameState.playerActions);
                  analyzeActionWrapper('4-Bet', fourBetAction)
               }
            } else {
               // Display a message if no actions were taken
               const noActionsMsg = document.createElement('p');
               noActionsMsg.textContent = 'No actions were recorded for this hand.';
               noActionsMsg.style.color = '#D4AF37';
               noActionsMsg.style.margin = '10px 0';
               this.handCompleteElement.appendChild(noActionsMsg);
            }
         }
      }
      
      // Add continue message
      const nextHandBtn = document.createElement('button');
      nextHandBtn.textContent = 'Next Hand';
      nextHandBtn.style.fontFamily = "'Georgia', 'Times New Roman', Times, serif";
      nextHandBtn.style.fontStyle = 'italic';
      nextHandBtn.style.fontSize = '20px';
      nextHandBtn.style.color = '#D4AF37';
      nextHandBtn.style.margin = '15px 0 5px 0';
      nextHandBtn.style.padding = '8px 16px';
      nextHandBtn.style.background = 'rgba(50, 40, 30, 0.8)';
      nextHandBtn.style.border = '1px solid #D4AF37';
      nextHandBtn.style.borderRadius = '5px';
      nextHandBtn.style.cursor = 'pointer';
      nextHandBtn.addEventListener('click', () => {
         // Dispatch a custom event that the game instance can listen for
         document.dispatchEvent(new CustomEvent('next-hand-request'));
      });
      this.handCompleteElement.appendChild(nextHandBtn);
   }   

   public hideHandCompleteMessage(): void {
      if (this.handCompleteElement) {
         this.handCompleteElement.style.display = 'none';
      }
   }

   private handleMouseDown(event: MouseEvent): void {
      const rect = this.canvas.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;

      // Check if clicked on a bet amount
      if (this.gameState && this.gameState.players) {
         const userPlayer = this.gameState.players.find((p: Player) => p.isUser);
         if (!userPlayer) return;
         
         for (let playerIdx = 0; playerIdx < this.gameState.players.length; playerIdx++) {
            const player = this.gameState.players[playerIdx];
            if (player.betAmount > 0) {
               const seatIndex = calculateRelativeSeatIndex(player, userPlayer);
               const betKey = `player${seatIndex}_bet`;
               const betPos = this.betPositions[betKey];
               
               // Check if the mouse click is within the bet circle boundary (radius of 12)
               if (betPos) {
                  const betRadius = 12;
                  const distance = Math.sqrt(Math.pow(x - betPos.x, 2) + Math.pow(y - betPos.y, 2));
                  if (distance <= betRadius) {
                     console.log(`Clicked on bet: player ${playerIdx}, at position ${betPos.x},${betPos.y}`);
                     this.isDragging = true;
                     this.selectedBet = seatIndex;
                     this.dragOffsetX = x - betPos.x;
                     this.dragOffsetY = y - betPos.y;
                     return;
                  }
               }
            }
         }
      }
      
      // Check if clicked on a card
      if (this.gameState && this.gameState.players) {
         // First, find the user player to determine relative positions
         const userPlayer = this.gameState.players.find((p: Player) => p.isUser);
         if (!userPlayer) return;
         
         for (let playerIdx = 0; playerIdx < this.gameState.players.length; playerIdx++) {
            const player = this.gameState.players[playerIdx];
            // Use appropriate card size based on player type
            const cardWidth = player.isUser ? this.userCardWidth : this.otherCardWidth;
            const cardHeight = player.isUser ? this.userCardHeight : this.otherCardHeight;
            
            // Check for each card
            for (let cardIdx = 0; cardIdx < 2; cardIdx++) {
               const cardKey = `player${playerIdx}_card${cardIdx}`;
               const cardPos = this.cardPositions[cardKey];
               
               // Ensure we have valid card position data
               if (cardPos) {
                  // Check if the mouse click is within the card boundaries
                  if (x >= cardPos.x && x <= cardPos.x + cardWidth &&
                      y >= cardPos.y && y <= cardPos.y + cardHeight) {
                     console.log(`Clicked on card: player ${playerIdx}, card ${cardIdx}, at position ${cardPos.x},${cardPos.y}`);
                     this.isDragging = true;
                     this.selectedCard = { playerIndex: playerIdx, cardIndex: cardIdx };
                     this.dragOffsetX = x - cardPos.x;
                     this.dragOffsetY = y - cardPos.y;
                     return;
                  }
               }
            }
         }
      }

      // If not clicked on a card or bet, check if clicked on a seat
      for (const seatId in this.seatPositions) {
         const seat = this.seatPositions[seatId];
         const distance = Math.sqrt(Math.pow(x - (seat.x + this.xOffset), 2) + Math.pow(y - (seat.y + this.yOffset), 2));

         if (distance <= this.seatRadius) {
            this.isDragging = true;
            this.selectedSeat = seatId;
            this.dragOffsetX = x - (seat.x + this.xOffset);
            this.dragOffsetY = y - (seat.y + this.yOffset);
            break;
         }
      }
   }

   private handleMouseMove(event: MouseEvent): void {
      if (!this.isDragging) return;

      const rect = this.canvas.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;

      if (this.selectedSeat) {
         // Update the position of the selected seat
         this.seatPositions[this.selectedSeat].x = x - this.dragOffsetX - this.xOffset;
         this.seatPositions[this.selectedSeat].y = y - this.dragOffsetY - this.yOffset;
      } else if (this.selectedCard) {
         // Update the position of the selected card
         const cardKey = `player${this.selectedCard.playerIndex}_card${this.selectedCard.cardIndex}`;
         this.cardPositions[cardKey] = {
            x: x - this.dragOffsetX,
            y: y - this.dragOffsetY
         };
      } else if (this.selectedBet !== null) {
         // Update the position of the selected bet
         const betKey = `player${this.selectedBet}_bet`;
         this.betPositions[betKey] = {
            x: x - this.dragOffsetX,
            y: y - this.dragOffsetY
         };
      }

      // Re-render the table
      if (this.gameState) {
         this.render(this.gameState);
      }
   }

   private handleMouseUp(_event: MouseEvent): void {
      if (this.isDragging) {
         if (this.selectedSeat) {
            const seat = this.seatPositions[this.selectedSeat];
            console.log(`Seat ${this.selectedSeat} position: { x: ${Math.round(seat.x)}, y: ${Math.round(seat.y)} }`);
         } else if (this.selectedCard) {
            const cardKey = `player${this.selectedCard.playerIndex}_card${this.selectedCard.cardIndex}`;
            const card = this.cardPositions[cardKey];
            console.log(`Card position for player ${this.selectedCard.playerIndex}, card ${this.selectedCard.cardIndex}: { x: ${Math.round(card.x)}, y: ${Math.round(card.y)} }`);
         } else if (this.selectedBet !== null) {
            const betKey = `player${this.selectedBet}_bet`;
            const bet = this.betPositions[betKey];
            console.log(`Bet position for player ${this.selectedBet}: { x: ${Math.round(bet.x)}, y: ${Math.round(bet.y)} }`);
         }
      }

      this.isDragging = false;
      this.selectedSeat = null;
      this.selectedCard = null;
      this.selectedBet = null;
   }

   private preloadCardImages(): void {
      // Use typed Rank and Suit from types.ts
      const ranks: Rank[] = ['A', 'K', 'Q', 'J', 'T', '9', '8', '7', '6', '5', '4', '3', '2'];
      const suits: Suit[] = ['H', 'D', 'C', 'S'];
      
      // Preload all 52 card images
      for (const rank of ranks) {
         for (const suit of suits) {
            const card: Card = { rank, suit };
            const imageSrc = getCardImageSrc(card);
            const imgKey = `${rank}_${suit}`;
            
            const img = new Image();
            img.src = imageSrc;
            this.cardImages[imgKey] = img;
         }
      }
      
      console.log('Preloaded all card images');
   }

   // Public method to draw just the poker table
   public drawEmptyTable(): void {
      this.clear();
      this.drawTable();
   }
}
